import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { toast } from 'react-toastify';
import { ApolloGqlTalentForTalentBoard } from '@bloomays-lib/types.shared';
import { Button } from '@bloomays-lib/ui.shared';
import ToastErrorContent from '../atoms/ToastErrorContent';
import useSynchroTalentFromATS from '../../hooks/useSynchroTalentFromATS';

type SynchroTalentFromATSProps = {
  talent: ApolloGqlTalentForTalentBoard;
  jobId: string;
};

const SynchroTalentFromATSButton = ({ talent, jobId }: SynchroTalentFromATSProps) => {
  const { t } = useTranslation('talentBoard');

  const { synchroTalentFromATS, loading: synchroLoading } = useSynchroTalentFromATS({
    talentId: talent.id,
    jobId,
  });

  const handleSynchroFromATS = async () => {
    const loadingToast = toast(
      <LoadingToastContainer>
        <CircularProgress size={20} sx={{ marginRight: '12px' }} />
        <span>{t('talentJob.synchroInProgress')}</span>
      </LoadingToastContainer>,
      {
        autoClose: false,
        hideProgressBar: false,
        toastId: 'synchro-loading',
      },
    );

    try {
      const synchroResult = await synchroTalentFromATS();

      if (!synchroResult) {
        toast.dismiss(loadingToast);
        toast.error(t('talentJob.synchroGenericError') as string, { autoClose: 5000, hideProgressBar: false });
        return;
      }

      // Notification pour le talent
      if (synchroResult.talent?.success) {
        toast.success(t('talentJob.talentSyncSuccess') as string, {
          autoClose: 3000,
          hideProgressBar: false,
          toastId: 'talent-sync-success',
        });
      } else if (synchroResult.talent?.errors && synchroResult.talent.errors.length > 0) {
        toast.error(
          <ToastErrorContent message={t('talentJob.talentSyncError')} errors={synchroResult.talent.errors} />,
          { autoClose: 5000, hideProgressBar: false, toastId: 'talent-sync-error' },
        );
      }

      // Notification pour le CV
      if (synchroResult.resume?.success) {
        toast.success(t('talentJob.resumeSyncSuccess') as string, {
          autoClose: 3000,
          hideProgressBar: false,
          toastId: 'resume-sync-success',
        });
      } else if (synchroResult.resume?.errors && synchroResult.resume.errors.length > 0) {
        toast.error(
          <ToastErrorContent message={t('talentJob.resumeSyncError')} errors={synchroResult.resume.errors} />,
          { autoClose: 5000, hideProgressBar: false, toastId: 'resume-sync-error' },
        );
      }

      // Notification pour les notes
      if (synchroResult.notes?.success) {
        toast.success(t('talentJob.notesSyncSuccess') as string, {
          autoClose: 3000,
          hideProgressBar: false,
          toastId: 'notes-sync-success',
        });
      } else if (synchroResult.notes?.errors && synchroResult.notes.errors.length > 0) {
        toast.error(<ToastErrorContent message={t('talentJob.notesSyncError')} errors={synchroResult.notes.errors} />, {
          autoClose: 5000,
          hideProgressBar: false,
          toastId: 'notes-sync-error',
        });
      }

      toast.dismiss(loadingToast);
    } catch {
      toast.dismiss(loadingToast);
      toast.error(t('talentJob.synchroGenericError') as string, { autoClose: 5000, hideProgressBar: false });
    }
  };

  return (
    <Button
      textButton={t('talentJob.synchroButton')}
      onClick={handleSynchroFromATS}
      color="primary"
      disable={synchroLoading}
    />
  );
};

const LoadingToastContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
}));

export default SynchroTalentFromATSButton;
export { SynchroTalentFromATSButton };
